import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { PlusIcon } from "lucide-react";
import { fromJS } from "immutable";
import Editor from "../../components/Editor";
import ActivityAttendee from "./../ActivityAttendee";
import InputFieldDebounce from "../../components/ui/InputFieldDebounce";
import SelectItem from "../../main/component/SelectItem";
import { DatePickerField, SelectField } from "../../components/ui";
import { getItem } from "../../api/ProjectApi";
import { buildTimeValues } from "../CalendarTimeline";
import ActivitySender from "../ActivitySender";

const LogEmailForm = ({
  isEditMode,
  projectId,
  eventData,
  onDataChange,
  validationErrors,
  editorInputDescriptionRef,
}) => {
  const { t } = useTranslation();

  const currentUser = useSelector((state: any) => state.userReducer.get("currentUser"));
  const currentUserEmail = currentUser.get("email");
  const members = useSelector((state: any) => state.projectReducer.getIn(["project", "members"]));
  const attributes = useSelector((state: any) => state.template.get("attributes"));

  const timelines = fromJS(buildTimeValues());

  useEffect(() => {
    if (!isEditMode) {
      const attendees = eventData.attendees || [];
      if (currentUserEmail) {
        if (!attendees.includes(currentUserEmail)) {
          attendees.push(currentUserEmail);
        }
        onDataChange("organizer", currentUserEmail);
      }
      if (eventData.contactId) {
        getItem(eventData.contactId)
          .then((data) => {
            const item = fromJS(data);
            if (data && item.getIn(["data", "email", "value"])) {
              attendees.push(item.getIn(["data", "email", "value"]));
            }
            onDataChange("attendees", attendees);
          })
          .catch(() => {
            onDataChange("attendees", attendees);
          });
      } else {
        onDataChange("attendees", attendees);
      }
    }
  }, [currentUserEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChangeField = (fieldId, fieldValue) => {
    onDataChange(fieldId, fieldValue);
  };

  const handleSelectSearchItem = (searchItem) => {
    const item = {
      value: searchItem.get("id"),
      label: searchItem.getIn(["data", "name", "value"]),
      type: searchItem.get("type"),
    };
    handleChangeField("item", item);
    if (searchItem.get("type") !== "SALE") {
      const attendeeEmail = searchItem.getIn(["data", "email", "value"]);
      const attendees = [...eventData.attendees];
      if (attendeeEmail && !attendees.includes(attendeeEmail)) {
        attendees.push(attendeeEmail);
      }
      handleChangeField("attendees", attendees);
    } else {
      const contactId =
        searchItem.getIn(["data", "contact", "value", "id"]) ||
        searchItem.getIn(["data", "contact", "value", "value"]);
      handleChangeField("contactId", contactId);
    }
  };

  const getTimePart = (timeString) => {
    const timeData =
      timelines.find((item) => item.get("value") == timeString) || fromJS({ value: "0" });
    return timeData.get("value") == "0" ? undefined : timeData.toJS();
  };

  return (
    <div className="min-w-2xl space-y-4">
      <div>
        <div className="w-full">
          <label className="block text-sm font-semibold text-gray-800 mb-2">
            {t("Date & Time")}
          </label>
          <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-2 w-full">
            <div className={"w-full sm:w-36"}>
              <DatePickerField
                value={eventData.start}
                onChange={(value) => {
                  handleChangeField("start", value);
                  handleChangeField("end", value);
                }}
                disabledSelectDate={{ before: new Date() }}
              />
            </div>
            <div className={"w-full sm:w-36"}>
              <SelectField
                value={getTimePart(eventData.startTime)}
                onChange={(selectedDate) => {
                  handleChangeField("startTime", selectedDate.value);
                  handleChangeField("endTime", selectedDate.value);
                }}
                options={timelines.toJS()}
                placeholder={t("Start")}
                isOptionSelected={(option, selectValue) => {
                  return selectValue && selectValue[0] && option.value == selectValue[0].value;
                }}
                filterOption={(option, inputValue) => {
                  if (!inputValue) return true;
                  return option.label.toLowerCase().includes(inputValue.toLowerCase());
                }}
                stylesOverride={{
                  menu: (provided) => ({
                    ...provided,
                    minWidth: "190px",
                    width: "auto",
                  }),
                  option: (provided) => ({
                    ...provided,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }),
                }}
              />
            </div>
          </div>
          {validationErrors.startTimeEndTime && (
            <p className="text-xs text-red-600 mt-1">{validationErrors.startTimeEndTime}</p>
          )}
        </div>
      </div>
      <div>
        <ActivitySender
          title={t("Sender")}
          addButtonText={t("Add Sender")}
          projectId={projectId}
          eventData={eventData}
          handleChange={handleChangeField}
        />
        {validationErrors.organizer && (
          <p className="text-xs text-red-600 mt-1">{validationErrors.organizer}</p>
        )}
      </div>
      <div>
        <ActivityAttendee
          title={t("Recipients")}
          addButtonText={t("Add Recipient")}
          projectId={projectId}
          eventData={eventData}
          handleChange={handleChangeField}
        />
        {validationErrors.attendees && (
          <p className="text-xs text-red-600 mt-1">{validationErrors.attendees}</p>
        )}
      </div>
      <div>
        <InputFieldDebounce
          label={t("Subject")}
          placeholder={t("Enter subject...")}
          value={eventData.summary}
          onChange={(value) => handleChangeField("summary", value)}
          errorMessage={validationErrors.summary}
        />
      </div>
      <div>
        <div className="w-full">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-semibold text-gray-800 mb-2">{t("Content")}</label>
            <div>
              <div
                className="mb-2 inline-flex items-center gap-1 text-xs px-1.5 py-1 rounded border border-gray-200 shadow-sm cursor-pointer"
                onClick={() => {
                  if (editorInputDescriptionRef.current) {
                    editorInputDescriptionRef.current.getModule("mention").openMenu("{{");
                  }
                }}
              >
                <PlusIcon className="w-3 h-3" />
                <span>{t("Add Variable")}</span>
              </div>
            </div>
          </div>
          <div className="max-w-2xl rounded-lg border border-gray-300 [&_.ql-container]:!border-0">
            <Editor
              editorInputRef={editorInputDescriptionRef}
              showToolbar={false}
              placeholder={t("Start typing... or @ to mention, {{ to show smart values")}
              minRows={4}
              members={members}
              attributes={attributes}
              editorContent={{
                htmlContent: eventData.description,
                contents: {},
              }}
              onChange={(content) => {
                handleChangeField("description", content.htmlContent);
              }}
            />
          </div>
          {validationErrors.description && (
            <p className="text-xs text-red-600 mt-1">{validationErrors.description}</p>
          )}
        </div>
      </div>
      <div>
        <div className="w-full">
          <label className="block text-sm font-semibold text-gray-800 mb-2">
            {t("Linked Item")}
          </label>
          <div className="flex items-center">
            <SelectItem
              selectedItem={eventData.item}
              onSelectItem={(searchItem) => {
                if (searchItem.get("id")) {
                  handleSelectSearchItem(searchItem);
                } else {
                  handleChangeField("item", {
                    type: searchItem.get("type"),
                  });
                }
              }}
            />
          </div>
          {validationErrors.linkItem && (
            <p className="text-xs text-red-600 mt-1">{validationErrors.linkItem}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default LogEmailForm;
